import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileBarChart } from "lucide-react";
import { api, apiError, fmtDateTime } from "../lib/api";

export default function ImportSummaryPage() {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/import-summary");
                setSummary(data);
            } catch (e) { toast.error(apiError(e)); }
            finally { setLoading(false); }
        })();
    }, []);

    if (loading) return <div className="mx-auto max-w-5xl p-6"><div className="h-40 rounded-xl bg-white animate-pulse" /></div>;
    if (!summary || !summary.sites) return <div className="mx-auto max-w-5xl p-6 text-sm text-gray-500">No import yet.</div>;

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8" data-testid="import-summary-page">
            <div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Part E · historical import</div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2">
                    <FileBarChart className="w-6 h-6" /> Import summary
                </h1>
                <div className="text-xs text-gray-500 mt-1">Last import: {fmtDateTime(summary.updated_at)} · Open flags: {summary.review_flags_total}</div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {(summary.sites || []).map((s) => (
                    <div key={s.site} className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid={`summary-site-${s.site}`}>
                        <div className="flex justify-between items-baseline">
                            <div className="text-sm font-bold text-[#1E3355]">{s.site}</div>
                            {s.flags > 0 && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">{s.flags} flag(s)</span>}
                        </div>
                        <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                            {Object.entries(s).filter(([k]) => k !== "site" && k !== "note").map(([k, v]) => (
                                <div key={k} className="contents">
                                    <dt className="text-gray-500 capitalize">{k.replace(/_/g, " ")}</dt>
                                    <dd className="text-right text-[#1E3355] font-mono">{Array.isArray(v) ? (v.length ? v.join(", ") : "—") : (v === null ? "—" : String(v))}</dd>
                                </div>
                            ))}
                        </dl>
                        {s.note && <div className="mt-3 text-xs text-gray-500 italic">{s.note}</div>}
                    </div>
                ))}
            </div>

            {summary.requisitions && (
                <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid="req-summary">
                    <div className="text-sm font-bold text-[#1E3355] mb-2">Requisitions imported</div>
                    <table className="w-full text-xs">
                        <tbody>
                            {Object.entries(summary.requisitions).map(([csv, count]) => (
                                <tr key={csv} className="border-t border-gray-100">
                                    <td className="p-2 text-gray-700">{csv}</td>
                                    <td className="p-2 text-right font-mono text-[#1E3355]">{count} lines</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
